import { useState, useCallback, useEffect, useRef } from "react";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useSequentialLoading = ({
  queries,
  context,
  queryParams = {},
}) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastQueryParams, setLastQueryParams] = useState(null);

  // Use refs to avoid dependency issues
  const queriesRef = useRef(queries);
  const contextRef = useRef(context);
  const queryParamsRef = useRef(queryParams);

  // Add render count tracking for debugging
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  // Update refs when props change
  useEffect(() => {
    const queriesChanged =
      JSON.stringify(queriesRef.current) !== JSON.stringify(queries);
    if (queriesChanged) {
      console.log("🔄 useSequentialLoading: queries object changed", {
        renderCount: renderCountRef.current,
        oldQueries: Object.keys(queriesRef.current || {}),
        newQueries: Object.keys(queries || {}),
      });
    }
    queriesRef.current = queries;
  }, [queries]);

  useEffect(() => {
    const contextChanged =
      JSON.stringify(contextRef.current) !== JSON.stringify(context);
    if (contextChanged) {
      console.log("🔄 useSequentialLoading: context changed", {
        renderCount: renderCountRef.current,
        oldReady: contextRef.current?.ready,
        newReady: context?.ready,
        contextKeys: Object.keys(context || {}),
      });
    }
    contextRef.current = context;
  }, [context]);

  useEffect(() => {
    const paramsChanged =
      JSON.stringify(queryParamsRef.current) !== JSON.stringify(queryParams);
    if (paramsChanged) {
      console.log("🔄 useSequentialLoading: queryParams changed", {
        renderCount: renderCountRef.current,
        oldParams: queryParamsRef.current,
        newParams: queryParams,
      });
    }
    queryParamsRef.current = queryParams;
  }, [queryParams]);

  const loadSingleQuery = useCallback(
    async (name, query, currentContext, currentQueryParams) => {
      console.log(`Starting to load: ${name}`);

      try {
        setLoading((prev) => ({ ...prev, [name]: true }));
        setError((prev) => ({ ...prev, [name]: false }));

        // Pass both context and any additional query parameters
        const result = await query(
          currentContext,
          ...Object.values(currentQueryParams)
        );

        setData((prev) => ({ ...prev, [name]: result }));
        console.log(`Successfully loaded: ${name}`, result);
        return { success: true, error: null };
      } catch (err) {
        console.error(`Error loading ${name}:`, err);
        setError((prev) => ({
          ...prev,
          [name]: err.message || "Unknown error",
        }));
        return { success: false, error: err };
      } finally {
        setLoading((prev) => ({ ...prev, [name]: false }));
      }
    },
    []
  );

  const handleRetry = useCallback(
    async (queryName) => {
      const currentQueries = queriesRef.current;
      const currentContext = contextRef.current;
      const currentQueryParams = queryParamsRef.current;

      if (!currentQueries[queryName] || !currentContext?.ready) {
        console.warn(
          `Cannot retry ${queryName}: query or context not available`
        );
        return;
      }

      console.log(`Retrying query: ${queryName}`);
      await loadSingleQuery(
        queryName,
        currentQueries[queryName],
        currentContext,
        currentQueryParams
      );
    },
    [loadSingleQuery]
  );

  const startLoading = useCallback(async () => {
    const currentQueries = queriesRef.current;
    const currentContext = contextRef.current;
    const currentQueryParams = queryParamsRef.current;

    if (!currentQueries || !currentContext?.ready) {
      console.log("📋 useSequentialLoading: Cannot start loading", {
        hasQueries: !!currentQueries,
        contextReady: currentContext?.ready,
        renderCount: renderCountRef.current,
      });
      return;
    }

    console.log("📋 useSequentialLoading: Starting parallel batch loading...", {
      renderCount: renderCountRef.current,
      queryCount: Object.keys(currentQueries).length,
      isLoading,
      isInitialized,
    });
    setIsLoading(true);
    setIsInitialized(true);

    const queryEntries = Object.entries(currentQueries);
    const totalQueries = queryEntries.length;
    const batchSize = 4; // Run 4 queries in parallel
    const delayBetweenQueries = 200; // 200ms delay between parallel queries

    setProgress({ current: 0, total: totalQueries });

    try {
      let completedQueries = 0;

      // Process queries in batches of 4
      for (let i = 0; i < queryEntries.length; i += batchSize) {
        const batch = queryEntries.slice(i, i + batchSize);
        const batchNumber = Math.floor(i / batchSize) + 1;
        console.log(
          `Processing batch ${batchNumber} (${batch.length} queries): ${batch
            .map(([name]) => name)
            .join(", ")}`
        );

        // Create promises for the current batch
        const batchPromises = batch.map(async ([name, query], index) => {
          // Add delay between parallel queries in the same batch (but not for single queries)
          if (index > 0 && batch.length > 1) {
            await delay(delayBetweenQueries);
          }

          const result = await loadSingleQuery(
            name,
            query,
            currentContext,
            currentQueryParams
          );

          // Update progress after each query completes
          completedQueries++;
          setProgress({ current: completedQueries, total: totalQueries });

          return { name, result };
        });

        // Wait for all queries in the current batch to complete
        const batchResults = await Promise.all(batchPromises);

        // Log batch completion
        console.log(
          `Batch ${batchNumber} completed: ${batchResults
            .map(
              ({ name, result }) =>
                `${name}(${result.success ? "success" : "failed"})`
            )
            .join(", ")}`
        );

        // Special handling for single-query batches (like the last one)
        if (batch.length === 1) {
          console.log(`Single query batch processed: ${batch[0][0]}`);
        }

        // No delay between batches - immediately start the next batch
      }

      console.log("📋 useSequentialLoading: Parallel batch loading completed", {
        renderCount: renderCountRef.current,
      });
    } catch (err) {
      console.error("Error during parallel batch loading:", err);
    } finally {
      setIsLoading(false);
    }
  }, [loadSingleQuery, isLoading, isInitialized]);

  // Initialize loading when context becomes ready
  useEffect(() => {
    const shouldInitialize =
      context?.ready && queries && !isInitialized && !isLoading;

    if (shouldInitialize) {
      console.log(
        "📋 useSequentialLoading: Context ready, initializing parallel loading...",
        {
          renderCount: renderCountRef.current,
        }
      );
      startLoading();
    }
  }, [context?.ready, queries, isInitialized, isLoading, startLoading]);

  // Handle queryParams changes separately
  useEffect(() => {
    const currentParamsString = JSON.stringify(queryParams);

    // Initialize lastQueryParams on first run if it's null
    if (lastQueryParams === null) {
      setLastQueryParams(currentParamsString);
      return;
    }

    const shouldReload =
      context?.ready &&
      queries &&
      isInitialized &&
      !isLoading &&
      lastQueryParams !== currentParamsString;

    if (shouldReload) {
      console.log(
        "📋 useSequentialLoading: Query params changed, reloading data...",
        {
          renderCount: renderCountRef.current,
          oldParams: lastQueryParams,
          newParams: currentParamsString,
        }
      );
      setLastQueryParams(currentParamsString);
      // Reset data and reload
      setData({});
      setError({});
      setLoading({});
      startLoading();
    }
  }, [
    queryParams,
    context?.ready,
    queries,
    isInitialized,
    isLoading,
    lastQueryParams,
    startLoading,
  ]);

  return {
    data,
    loading,
    error,
    progress,
    handleRetry,
    isLoading,
    isInitialized,
  };
};
